// ─────────────────────────────────────────────────────────────
//  DONNÉES DE LA DÉMONSTRATION — SCÉNARIO UNIQUE
//  Data Engineer principal · Données et analytique · Montréal
//
//  Toutes les données sont fictives. Un seul scénario traverse
//  les 25 écrans : mêmes critères du sourcing jusqu'au résultat
//  à 90 jours. C'est cette continuité qui rend la boucle
//  mesurable — ne pas introduire de critères ailleurs.
// ─────────────────────────────────────────────────────────────

/* ══ PIPELINE ════════════════════════════════════════════════ */

export type PipelineStep = {
  label: string
  state: 'completed' | 'current' | 'upcoming'
}

// « Consentir » et « Apprendre » sont les deux étapes qu'aucun ATS
// n'a. Elles doivent rester visibles dans le pipeline.
export const pipelineLabels = [
  'Définir',
  'Sourcer',
  'Consentir',
  'Évaluer',
  'Intégrer',
  'Apprendre',
]

function pipeline(currentIndex: number): PipelineStep[] {
  return pipelineLabels.map((label, index) => ({
    label,
    state:
      index < currentIndex
        ? 'completed'
        : index === currentIndex
          ? 'current'
          : 'upcoming',
  }))
}

/* ══ RECRUTEMENTS ═══════════════════════════════════════════ */

export type Recruitment = {
  id: string
  title: string
  /** Cadrage entreprise : département, PAS client. */
  department: string
  location: string
  hiringManager: string
  steps: PipelineStep[]
  consented: number
  nextAction: string
  nextActionTone: 'neutral' | 'accent' | 'attention' | 'learning'
}

export const recruitments: Recruitment[] = [
  {
    id: 'r1',
    title: 'Data Engineer principal',
    department: 'Données et analytique',
    location: 'Montréal · hybride',
    hiringManager: 'Simon Ouellet',
    steps: pipeline(5),
    consented: 3,
    nextAction: 'Apprentissage à valider',
    nextActionTone: 'learning',
  },
  {
    id: 'r2',
    title: 'Analyste principal, cybersécurité',
    department: 'Technologies',
    location: 'Montréal · hybride',
    hiringManager: 'Nadia Fortin',
    steps: pipeline(3),
    consented: 5,
    nextAction: 'Entrevues à planifier',
    nextActionTone: 'accent',
  },
  {
    id: 'r3',
    title: 'Conseillère principale, conformité',
    department: 'Conformité et risques',
    location: 'Québec',
    hiringManager: 'Pierre Lemieux',
    steps: pipeline(2),
    consented: 2,
    nextAction: 'En attente de consentement',
    nextActionTone: 'attention',
  },
  {
    id: 'r4',
    title: 'Gestionnaire, expérience client',
    department: 'Opérations',
    location: 'Laval',
    hiringManager: 'Julie Cardinal',
    steps: pipeline(1),
    consented: 0,
    nextAction: 'Stratégie de sourcing à approuver',
    nextActionTone: 'neutral',
  },
]

/* ══ CRITÈRES — RÉFÉRENTIEL UNIQUE ═════════════════════════════ */

export type CriterionType = 'filtrage' | 'scoring' | 'entrevue' | 'reussite'

export type Criterion = {
  id: string
  label: string
  types: CriterionType[]
  weight: number | null // null = éliminatoire
  essential: boolean
  rationale: string
}

export const criteria: Criterion[] = [
  {
    id: 'c1',
    label: 'Capacité à construire une architecture de données',
    types: ['scoring', 'entrevue', 'reussite'],
    weight: 25,
    essential: true,
    rationale:
      'Le mandat est une modernisation de plateforme, pas une maintenance. Construire est le geste central du poste.',
  },
  {
    id: 'c2',
    label: 'Autonomie dans un environnement peu structuré',
    types: ['scoring', 'entrevue', 'reussite'],
    weight: 20,
    essential: true,
    rationale:
      'Le périmètre est encore flou pour 6 à 9 mois. Le prédécesseur a quitté pour cette raison.',
  },
  {
    id: 'c3',
    label: 'Expérience en organisation en transformation',
    types: ['scoring', 'entrevue'],
    weight: 15,
    essential: false,
    rationale:
      'Corrélé à la rétention sur les deux derniers recrutements de cette équipe.',
  },
  {
    id: 'c4',
    label: 'Maîtrise des technologies prioritaires',
    types: ['filtrage', 'scoring', 'entrevue'],
    weight: 15,
    essential: true,
    rationale:
      'Spark, Airflow, dbt, Snowflake. Partiellement apprenable, d’où un poids modéré.',
  },
  {
    id: 'c5',
    label: 'Disponibilité pour présence à Montréal (1 j / semaine)',
    types: ['filtrage'],
    weight: null,
    essential: true,
    rationale: 'Contrainte opérationnelle de l’équipe. Éliminatoire, non pondéré.',
  },
  {
    id: 'c6',
    label: 'Motivation pour un rôle à forte initiative',
    types: ['entrevue', 'reussite'],
    weight: 15,
    essential: true,
    rationale:
      'Ne peut se vérifier qu’en entrevue. C’est le critère qui a renversé le classement.',
  },
  {
    id: 'c7',
    label: 'Potentiel de rétention',
    types: ['scoring', 'reussite'],
    weight: 10,
    essential: false,
    rationale: 'Estimé à partir des trajectoires antérieures. Confiance faible avant entrevue.',
  },
]

/** Critères écartés par Talenxia — affichés à l’utilisateur avec motif. */
export const excludedCriteria = [
  { label: 'Nombre d’années d’expérience comme seuil', reason: 'Ne prédit pas la réussite et exclut des profils performants' },
  { label: 'Établissement d’enseignement', reason: 'Facteur de biais socioéconomique sans lien démontré avec le résultat' },
  { label: 'Provenance du diplôme', reason: 'Risque de discrimination fondée sur l’origine' },
  { label: 'Disponibilité liée à la situation familiale', reason: 'Renseignement protégé, ne peut être collecté ni utilisé' },
]

/* ══ SOURCING ════════════════════════════════════════════════ */

export type Source = {
  id: string
  name: string
  priority: 'Élevée' | 'Moyenne' | 'Contextuelle'
  why: string
  reach: string
  limits: string
  confidence: 'Élevée' | 'Moyenne' | 'Faible'
  connector: 'actif' | 'disponible' | 'autorisation'
}

export const sources: Source[] = [
  { id: 's1', name: 'Registre interne de talents', priority: 'Élevée', why: 'Talents ayant déjà consenti · coût d’acquisition nul', reach: 'Tous profils', limits: 'Portée et durée du consentement à revalider', confidence: 'Élevée', connector: 'actif' },
  { id: 's2', name: 'GitHub', priority: 'Élevée', why: 'La capacité à construire se prouve par du code public', reach: 'Bâtisseurs, profils techniques actifs', limits: 'Activité publique ≠ compétence professionnelle', confidence: 'Moyenne', connector: 'actif' },
  { id: 's3', name: 'LinkedIn', priority: 'Élevée', why: 'Trajectoires et contexte d’entreprise', reach: 'Profils en poste, passifs', limits: 'Déclaratif non vérifié · conditions d’utilisation à valider', confidence: 'Moyenne', connector: 'autorisation' },
  { id: 's4', name: 'Candidatures antérieures', priority: 'Moyenne', why: 'Profils déjà évalués par l’organisation', reach: 'Tous profils', limits: 'Durée de conservation à vérifier', confidence: 'Moyenne', connector: 'actif' },
  { id: 's5', name: 'Références internes', priority: 'Élevée', why: 'Meilleur prédicteur de rétention observé', reach: 'Tous profils', limits: 'Risque d’homogénéité — surveillé', confidence: 'Élevée', connector: 'actif' },
]

/** Écran 9 — ce que le client voit AVANT consentement. Agrégé uniquement. */
export const preConsentStats = {
  analysed: 214,
  relevant: 42,
  highFit: 9,
  contacted: 5,
  replied: 2,
  fullConsent: 3,
  exclusions: [
    { reason: 'Employé actuel de l’organisation', count: 2 },
    { reason: 'Hors zone géographique', count: 6 },
    { reason: 'Désinscription active', count: 1 },
    { reason: 'Approché il y a moins de 90 jours', count: 3 },
    { reason: 'Critère éliminatoire non rencontré', count: 5 },
  ],
}

/* ══ TALENTS ═════════════════════════════════════════════════ */

export type TalentState =
  | 'identifie' | 'analyse' | 'preselectionne' | 'a_contacter' | 'contacte'
  | 'en_attente' | 'interesse' | 'refuse' | 'consentement_partiel'
  | 'consentement_complet' | 'visible_client' | 'invite' | 'entrevue_planifiee'
  | 'entrevue_realisee' | 'evalue' | 'recommande' | 'selectionne'
  | 'non_retenu' | 'integre' | 'suivi_en_cours' | 'resultat_confirme'
  | 'consentement_retire' | 'archive'

export type InfoStatus = 'confirme' | 'infere' | 'a_valider'

export type Consent = {
  /** C1 — être considéré par l’organisation nommée, pour ce poste. */
  c1: { granted: boolean; date: string; expires: string; scope: string }
  /** C2 — utiliser Talenxia. Options JAMAIS présélectionnées. */
  c2: { granted: boolean; options: string[]; date: string | null }
}

export type CriterionScore = {
  criterionId: string
  before: number
  after: number
  status: InfoStatus
}

export type Talent = {
  id: string
  name: string
  initials: string
  currentTitle: string
  location: string
  years: number
  technologies: string[]
  context: string
  availability: string
  expectations: string
  whyFit: string
  toValidate: string[]
  consent: Consent
  scoreBefore: number
  scoreAfter: number
  confidence: 'Faible' | 'Moyen' | 'Élevé'
  state: TalentState
  outcome: string
  criterionScores: CriterionScore[]
}

export const talents: Talent[] = [
  {
    id: 't-marie',
    name: 'Marie Lavoie',
    initials: 'ML',
    currentTitle: 'Data Engineer sénior',
    location: 'Montréal (Rosemont)',
    years: 8,
    technologies: ['Spark', 'Airflow', 'dbt', 'Snowflake', 'Python', 'Terraform'],
    context: 'A construit deux plateformes de données de zéro, dont une seule pendant 14 mois',
    availability: '4 semaines',
    expectations: '128 000 $ · intéressée par une composante variable',
    whyFit: 'Équilibre entre technique, autonomie et culture. Deux preuves documentées de construction.',
    toValidate: ['Profondeur en gouvernance de données', 'Encadrement d’une équipe'],
    consent: {
      c1: { granted: true, date: '12 sept. 2026', expires: '12 déc. 2026', scope: 'Ce poste uniquement' },
      // Refuse la communauté permanente — le parcours de refus partiel DOIT fonctionner.
      c2: { granted: true, options: ['Seulement cette opportunité'], date: '12 sept. 2026' },
    },
    scoreBefore: 84,
    scoreAfter: 89,
    confidence: 'Moyen',
    state: 'resultat_confirme',
    outcome: 'Sélectionnée · embauchée',
    criterionScores: [
      { criterionId: 'c1', before: 8, after: 9, status: 'confirme' },
      { criterionId: 'c2', before: 8, after: 9, status: 'confirme' },
      { criterionId: 'c3', before: 8, after: 8, status: 'confirme' },
      { criterionId: 'c4', before: 8, after: 8, status: 'confirme' },
      { criterionId: 'c5', before: 10, after: 10, status: 'confirme' },
      { criterionId: 'c6', before: 7, after: 9, status: 'confirme' },
      { criterionId: 'c7', before: 7, after: 8, status: 'infere' },
    ],
  },
  {
    id: 't-karim',
    name: 'Karim Belhadj',
    initials: 'KB',
    currentTitle: 'Architecte de données',
    location: 'Laval',
    years: 11,
    technologies: ['Spark', 'Airflow', 'Snowflake', 'dbt', 'Terraform', 'Kafka'],
    context: '7 ans en grande entreprise · équipes structurées, périmètre défini',
    availability: '6 semaines de préavis',
    expectations: '135 000 $ · souhaite un cadre clair',
    whyFit: 'Le meilleur niveau technique des trois. Maîtrise complète de la pile prioritaire.',
    toValidate: ['Motivation pour un rôle à forte initiative', 'Tolérance à l’ambiguïté'],
    consent: {
      c1: { granted: true, date: '10 sept. 2026', expires: '10 déc. 2026', scope: 'Ce poste uniquement' },
      c2: { granted: true, options: ['Cette organisation, 6 mois'], date: '10 sept. 2026' },
    },
    scoreBefore: 87,
    scoreAfter: 79,
    confidence: 'Moyen',
    state: 'non_retenu',
    outcome: 'Non retenu · maintenu au registre avec consentement',
    criterionScores: [
      { criterionId: 'c1', before: 9, after: 9, status: 'confirme' },
      { criterionId: 'c2', before: 8, after: 5, status: 'confirme' },
      { criterionId: 'c3', before: 6, after: 5, status: 'confirme' },
      { criterionId: 'c4', before: 10, after: 10, status: 'confirme' },
      { criterionId: 'c5', before: 10, after: 10, status: 'confirme' },
      { criterionId: 'c6', before: 7, after: 4, status: 'confirme' },
      { criterionId: 'c7', before: 8, after: 6, status: 'infere' },
    ],
  },
  {
    id: 't-theo',
    name: 'Théo Nguyen',
    initials: 'TN',
    currentTitle: 'Data Engineer',
    location: 'Sherbrooke · Montréal 1 j / semaine',
    years: 4,
    technologies: ['Python', 'dbt', 'Airflow', 'PostgreSQL', 'Spark (en apprentissage)'],
    context: 'Startup de 10 personnes · a construit seul toute la chaîne de données',
    availability: 'Immédiate',
    expectations: '98 000 $ · forte motivation d’apprentissage',
    whyFit: 'Autonomie exceptionnelle pour son niveau · preuve de construction complète.',
    toValidate: ['Volume de données traité', 'Expérience de mise à l’échelle'],
    consent: {
      c1: { granted: true, date: '11 sept. 2026', expires: '11 déc. 2026', scope: 'Ce poste uniquement' },
      c2: { granted: true, options: ['D’autres opportunités similaires', 'Communauté de talents Talenxia'], date: '11 sept. 2026' },
    },
    scoreBefore: 76,
    scoreAfter: 81,
    confidence: 'Faible',
    state: 'non_retenu',
    outcome: 'Non retenu pour ce poste · recontacté 5 mois plus tard',
    criterionScores: [
      { criterionId: 'c1', before: 7, after: 8, status: 'confirme' },
      { criterionId: 'c2', before: 8, after: 10, status: 'confirme' },
      { criterionId: 'c3', before: 7, after: 7, status: 'infere' },
      { criterionId: 'c4', before: 5, after: 5, status: 'confirme' },
      { criterionId: 'c5', before: 8, after: 8, status: 'confirme' },
      { criterionId: 'c6', before: 8, after: 10, status: 'confirme' },
      { criterionId: 'c7', before: 6, after: 7, status: 'a_valider' },
    ],
  },
]

/* ══ RÉSULTAT À 90 JOURS — ÉCRAN 23 ════════════════════════════ */

export type Verdict = 'confirme' | 'sous_estime' | 'sur_estime' | 'non_confirme'

export const ninetyDayResult = {
  talentId: 't-marie',
  hiredOn: '3 nov. 2026',
  reviewedOn: '1 févr. 2027',
  rows: [
    { criterionId: 'c1', predicted: 9, actual: 9, verdict: 'confirme' as Verdict, note: '' },
    { criterionId: 'c2', predicted: 9, actual: 10, verdict: 'sous_estime' as Verdict, note: 'A pris en charge un chantier hors périmètre' },
    { criterionId: 'c3', predicted: 8, actual: 8, verdict: 'confirme' as Verdict, note: '' },
    { criterionId: 'c4', predicted: 8, actual: 7, verdict: 'sur_estime' as Verdict, note: 'Écart comblé en 6 semaines' },
    { criterionId: 'c5', predicted: 10, actual: 10, verdict: 'non_confirme' as Verdict, note: 'Ne prédit pas la réussite — critère opérationnel, pas de performance' },
    { criterionId: 'c6', predicted: 9, actual: 9, verdict: 'confirme' as Verdict, note: '' },
    { criterionId: 'c7', predicted: 7, actual: 9, verdict: 'sous_estime' as Verdict, note: '' },
  ],
}

/* ══ APPRENTISSAGE — ÉCRAN 24 ════════════════════════════════ */

export const learningProposal = {
  status: 'propose' as 'propose' | 'valide' | 'refuse' | 'reporte',
  statement:
    'Pour les postes de données de cette organisation, l’autonomie et la capacité à construire prédisent mieux la réussite que le nombre d’années d’expérience ou la maîtrise initiale complète de la pile technologique.',
  changes: [
    { criterionId: 'c3', from: 15, to: 8, direction: 'baisse' as const },
    { criterionId: 'c2', from: 20, to: 26, direction: 'hausse' as const },
    { criterionId: 'c4', from: 15, to: 11, direction: 'baisse' as const },
  ],
  safeguards: [
    { label: 'Seuil minimal de données atteint', detail: '12 embauches comparables', ok: true },
    { label: 'Contrôle de biais effectué', detail: 'Aucune corrélation détectée avec une caractéristique protégée', ok: true },
    { label: 'Variables issues de la performance réelle', detail: 'Et non des préférences des décideurs', ok: true },
    { label: 'Validation humaine requise', detail: 'En attente de votre décision', ok: false },
    { label: 'Réversible', detail: 'Désactivable à tout moment · état antérieur conservé', ok: true },
  ],
}

/* ══ INTÉGRATION ════════════════════════════════════════════ */

export type Checkpoint = {
  label: string
  status: 'done' | 'due' | 'upcoming'
}

export type Onboarding = {
  id: string
  name: string
  title: string
  department: string
  checkpoints: Checkpoint[]
  alert?: string
}

export const onboardings: Onboarding[] = [
  {
    id: 'o1',
    name: 'Marie Lavoie',
    title: 'Data Engineer principale',
    department: 'Données et analytique',
    checkpoints: [
      { label: 'J+30', status: 'done' },
      { label: 'J+60', status: 'done' },
      { label: 'J+90', status: 'done' },
    ],
  },
  {
    id: 'o2',
    name: 'Sophie Bergeron',
    title: 'Analyste financière principale',
    department: 'Finance',
    checkpoints: [
      { label: 'J+30', status: 'done' },
      { label: 'J+60', status: 'due' },
      { label: 'J+90', status: 'upcoming' },
    ],
    alert: 'Écart attentes / réalité signalé',
  },
  {
    id: 'o3',
    name: 'Jean-Philippe Côté',
    title: 'Ingénieur fiabilité',
    department: 'Technologies',
    checkpoints: [
      { label: 'J+30', status: 'done' },
      { label: 'J+60', status: 'due' },
      { label: 'J+90', status: 'upcoming' },
    ],
  },
]

/* ══ INDICATEURS ═══════════════════════════════════════════ */

export const kpis = {
  postsToAction: 3,
  consentedWaiting: 12,
  onboardingDue: 2,
  retention90: '87 %',
  retentionBase: 'sur 23 embauches suivies',
  predictionAccuracy: '84 %',
  predictionBase: 'prédiction confirmée à 90 jours',
  consentedRegistry: 318,
}

export function criterionById(id: string) {
  return criteria.find((c) => c.id === id)
}
